import { ROUTE_PATHS } from '../../app/router/route-paths';
import type { CapabilitiesSnapshot, MeResponse } from '../../entities/me/me-contract.types';
import type { StaffBootstrapState } from '../bootstrap/bootstrap-state';
import { isRouteAllowed } from './access';
import { toBackendStaffSpaPath, toFrontendStaffPath } from './route-bridge';
import { STAFF_SPA_ROUTE_PATHS, type StaffSPARoutePath } from './staff-spa-route-paths';

/**
 * Ключи `routes_allowed` с `surfacePlatform` в `backend/core/usecase/staff_spa_routes.go`
 * (платформенный резерв; не использовать как первый экран после входа в организацию).
 */
export const STAFF_PLATFORM_SURFACE_ROUTE_PATHS: readonly StaffSPARoutePath[] = [
  '/superadmin/dashboard',
  '/superadmin/platform-users',
  '/superadmin/clients-catalog',
  '/superadmin/users',
  '/superadmin/users/platform',
  '/superadmin/users/clients',
  '/superadmin/users/roles',
  '/platform-organizations',
  '/platform-tenant-requests',
  '/platform-plans',
  '/admin-tokens',
  '/global-widget-theme',
  '/platform-qrcode-config',
  '/superadmin/payments',
  '/superadmin/assistant'
] as const;

const PLATFORM_SURFACE_SET = new Set<string>(STAFF_PLATFORM_SURFACE_ROUTE_PATHS);

/**
 * Снимок `superadminNoOrgAllowed` в Go (`staff_spa_routes.go`) — маршруты без контекста организации.
 */
export const SUPERADMIN_NO_ORG_STAFF_ROUTE_PATHS: readonly StaffSPARoutePath[] = [
  '/',
  '/superadmin/dashboard',
  '/superadmin/platform-users',
  '/superadmin/clients-catalog',
  '/superadmin/users',
  '/superadmin/users/platform',
  '/superadmin/users/clients',
  '/superadmin/users/roles',
  '/audit-log',
  '/audit-payments',
  '/pending-auth',
  '/bookings',
  '/payments',
  '/docs',
  '/contact',
  '/feedback',
  '/application',
  '/settings',
  '/payment-gateways',
  '/admin-tokens',
  '/platform-plans',
  '/tariffs',
  '/all-users',
  '/users',
  '/access-rbac',
  '/platform-organizations',
  '/platform-tenant-requests',
  '/superadmin/payments',
  '/superadmin/assistant',
  '/assistant',
  '/widget-embed-lab',
  '/global-widget-theme',
  '/platform-qrcode-config',
  '/fitness',
  '/me'
] as const;

const SUPERADMIN_NO_ORG_SET = new Set<string>(SUPERADMIN_NO_ORG_STAFF_ROUTE_PATHS);

/** Порядок выбора «домашнего» дашборда из `routes_allowed` (без role-check на фронте). */
export const STAFF_DASHBOARD_ROUTE_ORDER: readonly StaffSPARoutePath[] = [
  '/superadmin/dashboard',
  '/tenant/dashboard',
  '/staff/dashboard',
  '/fitness/dashboard'
] as const;

export function isStaffPlatformSurfaceRoute(backendKey: StaffSPARoutePath): boolean {
  return PLATFORM_SURFACE_SET.has(backendKey);
}

export function isSuperadminNoOrgShellRoute(backendKey: StaffSPARoutePath): boolean {
  return SUPERADMIN_NO_ORG_SET.has(backendKey);
}

/** Есть ли в снимке хотя бы один staff-маршрут, не относящийся только к платформенной поверхности. */
export function hasTenantFacingRouteInCapabilities(caps: CapabilitiesSnapshot | null | undefined): boolean {
  const list = caps?.routes_allowed;
  if (!list?.length) return false;
  for (const k of list) {
    if (!(STAFF_SPA_ROUTE_PATHS as readonly string[]).includes(k)) continue;
    if (isStaffPlatformSurfaceRoute(k as StaffSPARoutePath)) continue;
    return true;
  }
  return false;
}

function hasEffectiveOrganizationContext(me: MeResponse | null | undefined): boolean {
  const id = me?.organization_id;
  return id != null && id > 0;
}

function firstTenantRouteFromAllowed(allowed: Set<string>): string | null {
  for (const backendKey of STAFF_DASHBOARD_ROUTE_ORDER) {
    if (!allowed.has(backendKey)) continue;
    if (isStaffPlatformSurfaceRoute(backendKey as StaffSPARoutePath)) continue;
    return toFrontendStaffPath(backendKey);
  }
  for (const backendKey of STAFF_SPA_ROUTE_PATHS) {
    if (backendKey === '/') continue;
    if (!allowed.has(backendKey)) continue;
    if (isStaffPlatformSurfaceRoute(backendKey)) continue;
    return toFrontendStaffPath(backendKey);
  }
  return null;
}

function firstPlatformSurfaceFromAllowed(allowed: Set<string>): string | null {
  for (const backendKey of STAFF_PLATFORM_SURFACE_ROUTE_PATHS) {
    if (allowed.has(backendKey)) return toFrontendStaffPath(backendKey);
  }
  return null;
}

/**
 * Первый разрешённый путь для навигации после входа / смены контекста / имперсонации.
 * Источник — только `capabilities.routes_allowed` и `capabilities.staff_admin_shell` из `/auth/me` (без проверок по `me.role`).
 *
 * При **контексте организации** (`organization_id` в снимке `me`): сначала tenant-маршруты (порядок `STAFF_SPA_ROUTE_PATHS`),
 * затем платформенные из `routes_allowed`, затем `/superadmin` при `staff_admin_shell`, иначе `/forbidden`.
 *
 * Без контекста организации: 1) `/superadmin` при `staff_admin_shell.allowed`; 2) первый платформенный surface из `routes_allowed`;
 * 3) первый tenant-маршрут; 4) `/forbidden`.
 */
export function resolveFirstAllowedRoute(
  capabilities: CapabilitiesSnapshot | null | undefined,
  me?: MeResponse | null
): string {
  const allowed = new Set(capabilities?.routes_allowed ?? []);
  const shellOk = capabilities?.staff_admin_shell?.allowed === true;
  const orgCtx = hasEffectiveOrganizationContext(me);

  if (allowed.size === 0 && !shellOk) {
    return ROUTE_PATHS.forbidden;
  }

  if (orgCtx) {
    const tenant = firstTenantRouteFromAllowed(allowed);
    if (tenant) return tenant;
    const plat = firstPlatformSurfaceFromAllowed(allowed);
    if (plat) return plat;
    if (shellOk) {
      if (allowed.has('/superadmin/dashboard')) {
        return '/superadmin/dashboard';
      }
      return ROUTE_PATHS.superadmin;
    }
    return ROUTE_PATHS.forbidden;
  }

  if (shellOk) {
    if (allowed.has('/superadmin/dashboard')) {
      return '/superadmin/dashboard';
    }
    return ROUTE_PATHS.superadmin;
  }
  const platNoOrg = firstPlatformSurfaceFromAllowed(allowed);
  if (platNoOrg) return platNoOrg;
  const tenantNoOrg = firstTenantRouteFromAllowed(allowed);
  if (tenantNoOrg) return tenantNoOrg;
  return ROUTE_PATHS.forbidden;
}

/**
 * После входа: не восстанавливать «from», если он не проходит `routes_allowed` (например `/organization`
 * после выхода суперадмина с экрана имперсонации — иначе уводило на `/forbidden`).
 */
export function resolveStaffPostLoginDestination(
  fromPathname: string | undefined,
  capabilities: CapabilitiesSnapshot | null | undefined,
  me: MeResponse | null | undefined
): string {
  const fallback = resolveFirstAllowedRoute(capabilities, me);
  if (!fromPathname || fromPathname === ROUTE_PATHS.login) return fallback;
  const norm = fromPathname.replace(/\/+$/, '') || '/';
  if (norm === ROUTE_PATHS.login) return fallback;
  const bk = toBackendStaffSpaPath(norm);
  if (!bk) return fallback;
  if (!isRouteAllowed(capabilities, bk)) return fallback;
  return norm;
}

/**
 * Суперадмин без контекста организации на staff-маршруте вне allowlist `superadminNoOrgAllowed` (Go)
 * → каталог организаций при доступе к `/platform-organizations`, иначе `/superadmin`.
 */
export function getNoOrgShellRedirectPath(
  pathname: string,
  bootstrapKind: StaffBootstrapState['kind'],
  capabilities?: CapabilitiesSnapshot | null
): string | null {
  if (bootstrapKind !== 'authenticated_without_org_context') return null;
  const path = pathname.replace(/\/+$/, '') || '/';
  /** `/controls` обрабатывается в `ProtectedRoute` (только `isStaffPlatformMode`). */
  if (path === ROUTE_PATHS.controls) return null;
  const bk = toBackendStaffSpaPath(pathname);
  if (!bk) return null;
  if (isSuperadminNoOrgShellRoute(bk)) return null;
  return resolveFirstAllowedRoute(capabilities ?? null, null);
}

/**
 * Маршрут не в `routes_allowed`, сессия без орг., в capabilities нет tenant-маршрутов → безопасный shell вместо `/forbidden`.
 */
export function getDisallowedNoTenantCapsRedirect(
  bootstrapKind: StaffBootstrapState['kind'],
  capabilities: CapabilitiesSnapshot | null | undefined
): string | null {
  if (bootstrapKind !== 'authenticated_without_org_context') return null;
  if (hasTenantFacingRouteInCapabilities(capabilities)) return null;
  return resolveFirstAllowedRoute(capabilities ?? null, null);
}
